import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { shoppingCartState, ICartItem, SalesTaxEstimateAddress, IshipAddres } from '../../components/interfaces';
import { 
  fetchCartItem, 
  deleteCartItem, 
  decrementCartItemQuantity, 
  incrementCartItemQuantity,
  updateCartItemQuantity,
  createPaydOrder,
  deleteAllCartItem,
  fetchCartItemsCount,
  createCartItem,
  updateCurCartItemMaterial,
  updateCurCartItemSize,
  fetchMaterialPriceAndImagesForCart,
  estimateZipTax,
  fetchCartItemsSelectionsByIds,
} from '../../services';
import fallbackImage from '../../assets/img/ImgFV.jpg';

const guestCartStorageKey = 'truekarat_guest_cart_items';
const guestEmailStorageKey = 'truekarat_guest_email';
const guestShippingStorageKey = 'truekarat_guest_shipping_address';

const canUseStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage);

const readGuestCartItems = (): ICartItem[] => {
  if (!canUseStorage()) return [];

  try {
    const rawItems = window.localStorage.getItem(guestCartStorageKey);
    return rawItems ? JSON.parse(rawItems) : [];
  } catch {
    return [];
  }
};

const readGuestEmail = () => {
  if (!canUseStorage()) return '';
  return window.localStorage.getItem(guestEmailStorageKey) || '';
};

const readGuestShippingAddress = (): IshipAddres | undefined => {
  if (!canUseStorage()) return undefined;

  try {
    const rawAddress = window.localStorage.getItem(guestShippingStorageKey);
    return rawAddress ? JSON.parse(rawAddress) : undefined;
  } catch {
    return undefined;
  }
};

const persistGuestCheckout = (state: shoppingCartState) => {
  if (!canUseStorage()) return;

  window.localStorage.setItem(guestCartStorageKey, JSON.stringify(state.cartItems));
  window.localStorage.setItem(guestEmailStorageKey, state.guestEmail || '');

  if (state.guestShippingAddress) {
    window.localStorage.setItem(guestShippingStorageKey, JSON.stringify(state.guestShippingAddress));
  } else {
    window.localStorage.removeItem(guestShippingStorageKey);
  }
};

const recalculateCartState = (state: shoppingCartState) => {
  state.addedTooCartProductCountAndIds = {
    count: state.cartItems.length,
    ids: state.cartItems.map((item) => item.id),
  };

  state.totalAmount = state.cartItems.reduce((total, item) => {
    const materialPrice = state.materialPriceAndImgForCart.find(p => p.itemId === item.id)?.materialPrice;
    const fallbackPrice = item.materialPrice ?? item.productPrice ?? Number(item.product.price || 0);
    return total + Number(materialPrice ?? fallbackPrice ?? 0) * Number(item.quantity || 1);
  }, 0);
};

const initialState: shoppingCartState = {
  cartItems: [],
  addedTooCartProductCountAndIds: {
    count: 0,
    ids: [],
  },
  cartSelectionsById: {},
  status: 'idle',
  error: null,
  materialPriceAndImgForCart: [],
  imgStatus: 'idle',
  loading: false,
  order: undefined,
  userId: undefined,
  totalAmount: 0,
  totalItems: undefined,
  taxAmount: 0,
  taxRate: 0,
  taxStatus: 'idle',
  taxError: null,
  taxEstimateAddress: {
    province: '',
    country: 'USA',
  },
  loadingMaterialById: {},
  guestEmail: '',
  guestShippingAddress: undefined,
};

const shoppingCartSlice = createSlice({
  name: 'shoppingCart',
  initialState,
  reducers: {
    setUserId(state, action: PayloadAction<string>) {
      state.userId = action.payload;
    },
    setSizeError: (state, action: PayloadAction<{ itemId: string; error: string }>) => {
      const { itemId, error } = action.payload;
      const item = state.cartItems.find(cartItem => cartItem.id === itemId);
      if (item) {
        if (!item.sizeErrors) {
          item.sizeErrors = [];
        }
        if (!item.sizeErrors.includes(error)) {
          item.sizeErrors.push(error);
        }
      }
    },
    clearSizeError: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const item = state.cartItems.find(cartItem => cartItem.id === itemId);
      if (item) {
        item.sizeErrors = [];
      }
    },
    setCartItems(state, action: PayloadAction<ICartItem[]>) {
      state.cartItems = action.payload;
      state.totalAmount = state.cartItems.reduce((total, item) => {
        const materialPrice = state.materialPriceAndImgForCart.find(p => p.itemId === item.id)?.materialPrice ?? 0;
        return total + materialPrice * item.quantity;
      }, 0);
    },
    updateTotalAmount: (state, action: PayloadAction<{ cartItems: ICartItem[]; materialPriceAndImgForCart: typeof state.materialPriceAndImgForCart }>) => {
      const { cartItems, materialPriceAndImgForCart } = action.payload;
    
      state.totalAmount = cartItems.reduce((total, item) => {
        const materialPrice = materialPriceAndImgForCart.find(p => p.itemId === item.id)?.materialPrice ?? 0;
        return total + materialPrice * item.quantity;
      }, 0);
    },
    clearCart: (state) => {
      state.cartItems = [];
      state.addedTooCartProductCountAndIds.count = 0;
      state.addedTooCartProductCountAndIds.ids = [];
      state.totalAmount = 0;
      state.taxAmount = 0;
      state.taxRate = 0;
      state.taxStatus = 'idle';
      state.taxError = null;
      state.taxEstimateAddress = {
        province: '',
        country: 'USA',
      };
      state.guestEmail = '';
      state.guestShippingAddress = undefined;
      persistGuestCheckout(state);
    },
    loadGuestCheckout(state) {
      state.cartItems = readGuestCartItems();
      state.guestEmail = readGuestEmail();
      state.guestShippingAddress = readGuestShippingAddress();
      state.status = 'succeeded';
      recalculateCartState(state);
    },
    addGuestCartItem(state, action: PayloadAction<ICartItem>) {
      const nextItem = action.payload;
      const duplicate = state.cartItems.some(
        (item) =>
          item.product.id === nextItem.product.id &&
          item.selectedMaterial === nextItem.selectedMaterial &&
          (item.selectedSize || '') === (nextItem.selectedSize || '')
      );

      if (!duplicate) {
        state.cartItems.push(nextItem);
      }

      state.status = 'succeeded';
      recalculateCartState(state);
      persistGuestCheckout(state);
    },
    incrementGuestCartItemQuantity(state, action: PayloadAction<string>) {
      const item = state.cartItems.find(cartItem => cartItem.id === action.payload);
      if (item) {
        item.quantity += 1;
      }
      recalculateCartState(state);
      persistGuestCheckout(state);
    },
    decrementGuestCartItemQuantity(state, action: PayloadAction<string>) {
      const item = state.cartItems.find(cartItem => cartItem.id === action.payload);
      if (item && item.quantity > 1) {
        item.quantity -= 1;
      }
      recalculateCartState(state);
      persistGuestCheckout(state);
    },
    setCartItemQuantity(state, action: PayloadAction<{ itemId: string; quantity: number; persistGuest?: boolean }>) {
      const item = state.cartItems.find(cartItem => cartItem.id === action.payload.itemId);
      if (item) {
        item.quantity = Math.max(1, action.payload.quantity);
      }
      recalculateCartState(state);
      if (action.payload.persistGuest) {
        persistGuestCheckout(state);
      }
    },
    deleteGuestCartItem(state, action: PayloadAction<string>) {
      state.cartItems = state.cartItems.filter(item => item.id !== action.payload);
      recalculateCartState(state);
      persistGuestCheckout(state);
    },
    updateCartItemSizeLocal(state, action: PayloadAction<{ itemId: string; selectedSize: string; persistGuest?: boolean }>) {
      const item = state.cartItems.find(cartItem => cartItem.id === action.payload.itemId);
      if (item) {
        item.selectedSize = action.payload.selectedSize;
        item.sizeErrors = [];
      }
      if (action.payload.persistGuest) {
        persistGuestCheckout(state);
      }
    },
    updateCartItemMaterialLocal(state, action: PayloadAction<{ itemId: string; selectedMaterial: string; selectedSize?: string; persistGuest?: boolean }>) {
      const item = state.cartItems.find(cartItem => cartItem.id === action.payload.itemId);
      if (item) {
        item.selectedMaterial = action.payload.selectedMaterial;
        item.selectedSize = action.payload.selectedSize || '';
        item.sizeErrors = [];
      }
      if (action.payload.persistGuest) {
        persistGuestCheckout(state);
      }
    },
    updateGuestCartItemSize(state, action: PayloadAction<{ itemId: string; selectedSize: string }>) {
      const item = state.cartItems.find(cartItem => cartItem.id === action.payload.itemId);
      if (item) {
        item.selectedSize = action.payload.selectedSize;
        item.sizeErrors = [];
      }
      persistGuestCheckout(state);
    },
    updateGuestCartItemMaterial(state, action: PayloadAction<{ itemId: string; selectedMaterial: string; selectedSize?: string }>) {
      const item = state.cartItems.find(cartItem => cartItem.id === action.payload.itemId);
      if (item) {
        item.selectedMaterial = action.payload.selectedMaterial;
        item.selectedSize = action.payload.selectedSize || '';
        item.sizeErrors = [];
      }
      persistGuestCheckout(state);
    },
    setGuestEmail(state, action: PayloadAction<string>) {
      state.guestEmail = action.payload;
      persistGuestCheckout(state);
    },
    setGuestShippingAddress(state, action: PayloadAction<IshipAddres>) {
      state.guestShippingAddress = action.payload;
      persistGuestCheckout(state);
    },
    setTaxEstimateAddress(state, action: PayloadAction<Partial<SalesTaxEstimateAddress>>) {
      state.taxEstimateAddress = {
        ...state.taxEstimateAddress,
        ...action.payload,
      };
    },
    resetTaxEstimate(state) {
      if (
        state.taxAmount === 0 &&
        state.taxRate === 0 &&
        state.taxStatus === 'idle' &&
        state.taxError === null
      ) {
        return;
      }

      state.taxAmount = 0;
      state.taxRate = 0;
      state.taxStatus = 'idle';
      state.taxError = null;
    },
    setTaxEstimateResult(state, action: PayloadAction<{ amount: number; rate: number }>) {
      state.taxAmount = action.payload.amount;
      state.taxRate = action.payload.rate;
      state.taxStatus = 'succeeded';
      state.taxError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartItemsCount.fulfilled, (state, action) => {
        state.addedTooCartProductCountAndIds = {
          count: action.payload.count,
          ids: action.payload.ids,
        };
      })
      .addCase(fetchCartItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCartItem.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.cartItems = action.payload;
        state.totalAmount = state.cartItems.reduce((total, item) => {
          const materialPrice = state.materialPriceAndImgForCart.find(p => p.itemId === item.id)?.materialPrice ?? 0;
          return total + (materialPrice * item.quantity);
        }, 0);
      })
      .addCase(fetchCartItem.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.status = 'failed';
        state.error = action.payload || 'Unknown error occurred';
      })
      .addCase(createCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCartItem.fulfilled, (state, action) => {
        const newItemId = action.payload.id;
      
        if (!state.addedTooCartProductCountAndIds.ids.includes(newItemId)) {
          state.addedTooCartProductCountAndIds.ids.push(newItemId);
          state.addedTooCartProductCountAndIds.count += 1;
        }
        state.loading = false;
      })
      .addCase(createCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create cart item';
      })
      .addCase(deleteCartItem.fulfilled, (state, action) => {
        const index = state.cartItems.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.cartItems.splice(index, 1);
          state.addedTooCartProductCountAndIds.ids =
            state.addedTooCartProductCountAndIds.ids.filter((id) => id !== action.payload.id);
          state.totalAmount = state.cartItems.reduce((total, item) => {
            const materialPrice = state.materialPriceAndImgForCart.find(p => p.itemId === item.id)?.materialPrice ?? 0;
            return total + (materialPrice * item.quantity);
          }, 0);
          state.status = 'succeeded';
          state.addedTooCartProductCountAndIds.count =
            state.addedTooCartProductCountAndIds.ids.length;
        }
      })
      .addCase(deleteCartItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || null;
      })
      .addCase(incrementCartItemQuantity.fulfilled, (state, action) => {
        const index = state.cartItems.findIndex(item => item.id === action.meta.arg.cartItemId);
        if (index !== -1) {
          state.cartItems[index].quantity = action.meta.arg.currentQuantity + 1;
          state.totalAmount = state.cartItems.reduce((total, item) => {
            const materialPrice = state.materialPriceAndImgForCart.find(p => p.itemId === item.id)?.materialPrice ?? 0;
            return total + (materialPrice * item.quantity);
          }, 0);
        }
      })
      .addCase(decrementCartItemQuantity.fulfilled, (state, action) => {
        const index = state.cartItems.findIndex(item => item.id === action.meta.arg.cartItemId);
        if (index !== -1 && action.meta.arg.currentQuantity > 1) {
          state.cartItems[index].quantity = action.meta.arg.currentQuantity - 1;
          state.totalAmount = state.cartItems.reduce((total, item) => {
            const materialPrice = state.materialPriceAndImgForCart.find(p => p.itemId === item.id)?.materialPrice ?? 0;
            return total + (materialPrice * item.quantity);
          }, 0);
        }
      })
      .addCase(updateCartItemQuantity.fulfilled, (state, action) => {
        const index = state.cartItems.findIndex(item => item.id === action.meta.arg.cartItemId);
        if (index !== -1) {
          state.cartItems[index].quantity = Math.max(1, action.meta.arg.quantity);
          recalculateCartState(state);
        }
      })
      .addCase(updateCurCartItemSize.fulfilled, (state, action) => {
        const { id, selectedSize } = action.payload;
        const cartItem = state.cartItems.find((item) => item.id === id);
        if (cartItem) {
          cartItem.selectedSize = selectedSize;
          cartItem.sizeErrors = [];
        }
      })      
      .addCase(updateCurCartItemMaterial.fulfilled, (state, action) => {
        const { id, selectedMaterial, selectedSize } = action.payload.updateCartItem;
        const itemIndex = state.cartItems.findIndex(item => item.id === id);
      
        if (itemIndex !== -1) {
          state.cartItems[itemIndex] = {
            ...state.cartItems[itemIndex],
            selectedMaterial,
            selectedSize,
            sizeErrors: [],
          };
        } else {
          console.warn('Item not found for ID:', id);
        }
      })
      .addCase(fetchMaterialPriceAndImagesForCart.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        state.imgStatus = 'loading';
        state.loadingMaterialById[itemId] = true;
        state.error = null;
      })
      .addCase(fetchMaterialPriceAndImagesForCart.fulfilled, (state, action) => {
        const { productId, selectedMaterial, itemId } = action.meta.arg;
        const materialPrices = action.payload?.product?.materialPrices || [];
      
        if (materialPrices.length > 0) {
          const { price, productImages, stockQuantity, isAvailable, variantInventories } = materialPrices[0];
      
          const imageObj = productImages?.[0]?.image;
          const firstImage = imageObj?.publicUrl
            ? { image: { publicUrl: imageObj.publicUrl, id: String(imageObj.id) } }
            : { image: { publicUrl: fallbackImage, id: 'default-id' } };
      
          const existingIndex = state.materialPriceAndImgForCart.findIndex(
            (item) => item.itemId === itemId
          );
      
          const newEntry = {
            itemId,
            productId,
            materialPrice: price,
            stockQuantity,
            isAvailable,
            variantInventories,
            materialImages: [firstImage],
            selectedMaterial,
          };
      
          if (existingIndex !== -1) {
            state.materialPriceAndImgForCart[existingIndex] = {
              ...state.materialPriceAndImgForCart[existingIndex],
              ...newEntry,
            };
          } else {
            state.materialPriceAndImgForCart.push(newEntry);
          }
        }
      
        state.loadingMaterialById[itemId] = false;
        state.imgStatus = 'succeeded';
      })
      .addCase(fetchMaterialPriceAndImagesForCart.rejected, (state, action) => {
        const { itemId } = action.meta.arg;
        state.loadingMaterialById[itemId] = false;
        state.imgStatus = 'failed';
        state.error = action.payload || 'Failed to fetch material price and images';
      })
      .addCase(updateCurCartItemMaterial.rejected, (state, action) => {
        console.error('Failed to update cart item material:', action.error.message);
        state.error = action.payload || 'Failed to update cart item material';
        state.loading = false;
      })
      .addCase(createPaydOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaydOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.order = action.payload;
      })
      .addCase(createPaydOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create order';
      })
      .addCase(deleteAllCartItem.fulfilled, (state) => {
        state.cartItems = [];
        state.totalAmount = 0;
        state.taxAmount = 0;
        state.taxRate = 0;
        state.taxStatus = 'idle';
        state.taxError = null;
        state.taxEstimateAddress = {
          province: '',
          country: 'USA',
        };
      })
      .addCase(deleteAllCartItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to delete cart items';
      })
      .addCase(fetchCartItemsSelectionsByIds.fulfilled, (state, action) => {
        action.payload.forEach((item) => {
          state.cartSelectionsById[item.id] = {
            productId: item.product.id,
            selectedSize: item.selectedSize,
            selectedMaterial: item.selectedMaterial,
          };
        });
      })
      .addCase(estimateZipTax.pending, (state) => {
        state.taxStatus = 'loading';
        state.taxError = null;
      })
      .addCase(estimateZipTax.fulfilled, (state, action) => {
        state.taxStatus = 'succeeded';
        state.taxAmount = action.payload.amount;
        state.taxRate = action.payload.rate;
      })
      .addCase(estimateZipTax.rejected, (state, action) => {
        state.taxStatus = 'failed';
        state.taxAmount = 0;
        state.taxRate = 0;
        state.taxError = action.payload || action.error.message || 'Failed to estimate tax';
      });
  },
});

export const { 
  setUserId, 
  setCartItems, 
  clearSizeError, 
  setSizeError, 
  updateTotalAmount, 
  clearCart,
  loadGuestCheckout,
  addGuestCartItem,
  incrementGuestCartItemQuantity,
  decrementGuestCartItemQuantity,
  setCartItemQuantity,
  deleteGuestCartItem,
  updateCartItemSizeLocal,
  updateCartItemMaterialLocal,
  updateGuestCartItemSize,
  updateGuestCartItemMaterial,
  setGuestEmail,
  setGuestShippingAddress,
  setTaxEstimateAddress,
  resetTaxEstimate,
  setTaxEstimateResult,
} = shoppingCartSlice.actions;

export default shoppingCartSlice.reducer;
